import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { actions, containers, elements, feedback, overlays } from '@ecosystem/newron-design-system';

import { ClientSidebar } from '../../content';
import { ConfigItem, ConfigItemValue } from '../../config-items/types';
import { ConfigItemElement } from '../../config-items/components/ConfigItemElement';
import { ConfigItemsTrayContextProvider } from '../../config-items/components/ConfigItemsTrayContextProvider';
import { GetUsersFunction, UserToShare } from '../modals/types';
import { Filter, FilterContext, FilterPermissions, SavedFilter, CreateFilter } from '../types';

import { SavedFilters } from './SavedFilters';
import { getUsers } from '../getUsers';
import { useLogging } from '../../logging';
import { getFilters } from '../getFilters';
import { FilterTrayContext } from './context';
import { SaveFilterModal } from '../modals';
import { createFilter } from '../createFilter';
import { buildFilterCriteria, buildFilterCriteriaGroups } from '../utils';

const { Button, IconButton, ButtonGroup } = actions;
const { Tray } = containers;
const { DotsLoader } = feedback;
const { icons: Icons } = elements.Icon;
const { Toast } = overlays;

export const DUPLICATE_NAME_API_ERROR = 'Name already exists';
export const UNIQUE_NAME_ERROR_MESSAGE = 'You cannot have two saved filters with the same name';
export const SAVE_ERROR_MESSAGE = 'Error while saving the filter';
export const FILTER_SAVED_MESSAGE = (filterName: string): string => `${filterName} has been successfully saved`;

export enum FilterTrayTabId {
  ConfigureFilters = 'configure-filters',
  SavedFilters = 'saved-filters',
}

export type MapToSavedFilterCallback = (filters: Filter[]) => SavedFilter[] | Promise<SavedFilter[]>;
export type GetFilterDataCallback = () => Record<string, unknown> | Promise<Record<string, unknown>>;

export interface FilterTrayProps {
  expanded?: boolean;
  configItems: ConfigItem[];
  configItemsLoading?: boolean;
  configItemsError?: string;
  filterContext: Partial<FilterContext>;
  /**
   * Indicates whether to use the new filter layout in SaveFilter modal.
   * Disclaimer: This should go away as the propositions start consuming the new filter tray.
   */
  useNewFilterLayout?: boolean;
  mapToSavedFilters: MapToSavedFilterCallback;
  getFilterData: GetFilterDataCallback;
  onValueChange: (configValue: ConfigItemValue) => void;
  onRequestOptions?: (key: string) => void;
  onClose?: () => void;
  onApplyConfig: () => void;
  onSavedFilterApplied: (filter: Filter) => void;
}

/**
 * WIP: This component is a work in progress, not fully functional and subject to change.
 */
export const FilterTray = ({
  expanded = false,
  configItems,
  configItemsLoading = false,
  configItemsError,
  filterContext,
  mapToSavedFilters,
  onValueChange,
  onRequestOptions,
  onClose,
  onApplyConfig,
  onSavedFilterApplied,
  getFilterData,
  useNewFilterLayout = false,
}: FilterTrayProps) => {
  const { closeSidebar, hideSidebar } = ClientSidebar.useSidebar({ key: 'filter-tray', expanded });
  const { error: logError } = useLogging();
  const [toast, setToast] = useState<{ type: overlays.Notification; message: string | React.ReactNode }>();
  const [dismissToast, setDismissToast] = useState(false);

  const [rawSavedFilters, setRawSavedFilters] = useState<Filter[]>([]);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [savedFiltersError, setSavedFiltersError] = useState<boolean>(false);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(true);
  const [shareUsers, setShareUsers] = useState<UserToShare[]>();
  const [activeTab, setActiveTab] = useState<string>();
  const [saveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [appliedFilter, setAppliedFilter] = useState<SavedFilter | null>(null);

  const filterTrayContext = useMemo(
    () => ({
      setToast,
    }),
    []
  );

  useEffect(() => {
    if (dismissToast) {
      setToast(undefined);
    }
    return () => {
      setDismissToast(false);
    };
  }, [dismissToast]);

  useEffect(() => {
    const loadSavedFilters = async () => {
      setSavedFiltersLoading(true);
      try {
        const filters = await getFilters(filterContext);
        setRawSavedFilters(filters);
      } catch (error) {
        logError(error, { message: 'Failed to fetch saved filters' });
        setSavedFiltersError(true);
      } finally {
        setSavedFiltersLoading(false);
      }
    };
    loadSavedFilters();
  }, [filterContext, logError]);

  useEffect(() => {
    setSavedFilters(prevFilters => {
      return prevFilters.map(filter =>
        filter.id === appliedFilter?.id ? { ...filter, isApplied: true, isNew: false } : { ...filter, isApplied: false }
      );
    });
  }, [appliedFilter]);

  useEffect(() => {
    if (!rawSavedFilters || rawSavedFilters.length === 0) {
      return;
    }
    const updateSavedFilters = async () => {
      const filters = await mapToSavedFilters(rawSavedFilters);
      setSavedFilters(filters);
    };
    updateSavedFilters();
    // Here, we expect an update of mapToSavedFilters when proposition's context is changed. (eg. multi cell selection for market)
    // When this happens, we want to re-map the filters using proposed mapToSavedFilters callback to have updated properties in saved filters (isAvailable).
    // This is the reason for having 2 distinct states: rawSavedFilters and savedFilters. (to avoid calling getFilters again)
  }, [rawSavedFilters, mapToSavedFilters]);

  // we are not calling onClose() because that would persist the closed state in session storage
  useEffect(() => {
    return () => hideSidebar();
  }, [hideSidebar]);

  const handleClose = () => {
    closeSidebar();
    onClose?.();
  };

  const getShareUsers: GetUsersFunction = useCallback(async () => {
    if (shareUsers) {
      return shareUsers;
    }

    try {
      const users = await getUsers();
      setShareUsers(users);
      return users;
    } catch (error) {
      logError(error, { message: 'Failed to fetch users' });
      return [];
    }
  }, [shareUsers, logError]);

  const handleApplyConfig = useCallback(() => {
    onApplyConfig();
    setAppliedFilter(null);
  }, [onApplyConfig]);

  const onSavedFilterDeleted = useCallback((filterId: string) => {
    setSavedFilters(prevFilters => prevFilters.filter(filter => filter.id !== filterId));
  }, []);

  const onSavedFilterShared = useCallback((filterId: string, updatedPermissions: FilterPermissions[]) => {
    setSavedFilters(prevFilters =>
      prevFilters.map(filter => (filter.id === filterId ? { ...filter, permissions: updatedPermissions } : filter))
    );
  }, []);

  const handleSavedFilterApplied = useCallback(
    (filter: SavedFilter) => {
      onSavedFilterApplied(rawSavedFilters.find(f => f.id === filter.id) ?? filter);
      setAppliedFilter(filter);
    },
    [onSavedFilterApplied, rawSavedFilters]
  );

  const handleCloseModal = useCallback(() => {
    setSaveFilterModalOpen(false);
    setErrorMessage('');
  }, []);

  const handleSaveFilter = useCallback(
    async (filterName: string) => {
      try {
        const filterData = await getFilterData();
        const filter: CreateFilter = {
          name: filterName,
          context: filterContext as FilterContext,
          data: filterData,
        };

        const createdFilter = await createFilter(filter);
        const filterPermissions = createdFilter.permissions.map(permission => ({
          user: {
            id: permission.user.user_id,
            givenName: permission.user.given_name,
            familyName: permission.user.family_name,
            email: permission.user.email,
          },
          access: permission.access,
        }));
        const filterCriteria = buildFilterCriteria(configItems);
        setSavedFilters(prevFilters => [
          ...prevFilters,
          {
            ...filter,
            id: createdFilter.id,
            criteria: filterCriteria,
            permissions: filterPermissions,
            isNew: true,
          },
        ]);
        setErrorMessage('');
        setToast({
          type: 'success',
          message: FILTER_SAVED_MESSAGE(filter.name),
        });
        handleCloseModal();
      } catch (error) {
        if (error.response?.status === 422 && error.response?.data?.message === DUPLICATE_NAME_API_ERROR) {
          setErrorMessage(UNIQUE_NAME_ERROR_MESSAGE);
        } else {
          setErrorMessage(SAVE_ERROR_MESSAGE);
          logError(error, { message: 'Failed to save filter' });
        }
      }
    },
    [configItems, logError, filterContext, getFilterData, handleCloseModal]
  );

  return (
    <ClientSidebar.Content>
      <ConfigItemsTrayContextProvider>
        <FilterTrayContext.Provider value={filterTrayContext}>
          <Tray>
            <Tray.Header>
              <div className="flex justify-between w-full h-14 items-center ">
                <p className="text-base">Filters</p>
                <IconButton
                  color="secondaryInteractive"
                  onClick={handleClose}
                  variant="bgLight"
                  mode="tertiary"
                  testId="close-filter-tray"
                >
                  <Icons.Close />
                </IconButton>
              </div>
            </Tray.Header>
            <Tray.Tabs currentTab={FilterTrayTabId.ConfigureFilters} onTabChange={setActiveTab}>
              <Tray.Tabs.Tab id={FilterTrayTabId.ConfigureFilters}>Configure</Tray.Tabs.Tab>
              <Tray.Tabs.Content id={FilterTrayTabId.ConfigureFilters}>
                {configItemsLoading && <DotsLoader testId="dots-loader" />}
                {configItems?.map((configItem, index) => (
                  <ConfigItemElement
                    key={configItem.key}
                    index={index}
                    configItem={configItem}
                    onChange={(key, value) => onValueChange({ key, value })}
                    onRequestOptions={key => onRequestOptions?.(key)}
                    depth={0}
                  />
                ))}
              </Tray.Tabs.Content>
              <Tray.Tabs.Tab id={FilterTrayTabId.SavedFilters}>Saved</Tray.Tabs.Tab>
              <Tray.Tabs.Content id={FilterTrayTabId.SavedFilters}>
                <SavedFilters
                  savedFilters={savedFilters}
                  loading={savedFiltersLoading}
                  savedFiltersError={savedFiltersError}
                  getUsers={getShareUsers}
                  onSavedFilterApplied={handleSavedFilterApplied}
                  onSavedFilterDeleted={onSavedFilterDeleted}
                  onSavedFilterShared={onSavedFilterShared}
                />
              </Tray.Tabs.Content>
            </Tray.Tabs>
            {activeTab === FilterTrayTabId.ConfigureFilters && (
              <Tray.Footer errorMessage={configItemsError}>
                <div className="flex-grow">
                  <ButtonGroup>
                    <ButtonGroup.PrimaryButton>
                      <Button
                        onClick={handleApplyConfig}
                        stretch
                        testId="apply-config-values"
                        disabled={!!configItemsError || configItemsLoading}
                      >
                        Apply
                      </Button>
                    </ButtonGroup.PrimaryButton>
                    <ButtonGroup.SecondaryButtons>
                      <Button
                        onClick={() => setSaveFilterModalOpen(true)}
                        mode="secondary"
                        stretch
                        testId="save-filter"
                      >
                        Save filter
                      </Button>
                    </ButtonGroup.SecondaryButtons>
                  </ButtonGroup>
                </div>
              </Tray.Footer>
            )}
          </Tray>
        </FilterTrayContext.Provider>
      </ConfigItemsTrayContextProvider>
      {toast && (
        <Toast type={toast.type} onComplete={() => setDismissToast(true)}>
          {toast.message}
        </Toast>
      )}
      {saveFilterModalOpen && (
        <SaveFilterModal
          isOpen={saveFilterModalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveFilter}
          filterCriteria={buildFilterCriteriaGroups(configItems)}
          filterNameErrorMessage={errorMessage}
          useNewFilterLayout={useNewFilterLayout}
        />
      )}
    </ClientSidebar.Content>
  );
};
